import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';
import useWorkspaceChange from '../useWorkspaceChange';

interface SavedQuery {
    QUERY_ID: string;
    USER_ID: string;
    APPLICATION_DATE: string;
    QUERY_TYPE: string;
    EXECUTION_DATE: string | null;
    DESCRIPTION: string;
    SOURCE: string;
    UPDATE_DATE: string;
    STATUS_ID: string;
    LIMIT: string;
    IS_FROM_COMPANY: string;
    GROUP_QUERY_ID: string;
    QUERY_GROUP_NAME: string;
    COMMENT_QUERY: string | null;
    QUERY_NAME: string;
    json_query?: unknown;
    conditions?: unknown;
}

interface SavedQueriesResponse {
    totalItems: number;
    member: SavedQuery[];
    view: {
        first: string;
        last: string;
    };
}

interface UseSavedQueriesOptions {
    page?: number;
    itemsPerPage?: number;
    groupQueryIds?: string;
    isFromCompany?: 0 | 1;
    search?: string;
}

interface SavedQueriesCacheData {
    queries: SavedQuery[];
    totalItems: number;
}

const buildQueriesCacheKey = (opts: UseSavedQueriesOptions, wsId?: string | null): string =>
    `saved_queries|${wsId ?? ''}|${opts.page ?? ''}|${opts.itemsPerPage ?? ''}|${opts.groupQueryIds ?? ''}|${opts.isFromCompany ?? ''}|${opts.search ?? ''}`;

export const useSavedQueries = (options: UseSavedQueriesOptions = {}) => {
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();

    const cacheKey = useMemo(() => buildQueriesCacheKey(options, workspaceId),
        [options.page, options.itemsPerPage, options.groupQueryIds, options.isFromCompany, options.search, workspaceId] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const [queries, setQueries] = useState<SavedQuery[]>(() =>
        cache?.get<SavedQueriesCacheData>(buildQueriesCacheKey(options, workspaceId))?.data?.queries ?? []
    );
    const [totalItems, setTotalItems] = useState<number>(() =>
        cache?.get<SavedQueriesCacheData>(buildQueriesCacheKey(options, workspaceId))?.data?.totalItems ?? 0
    );
    const [loading, setLoading] = useState<boolean>(() =>
        !cache?.get(buildQueriesCacheKey(options, workspaceId))
    );

    const [error, setError] = useState<string | null>(null);

    const fetchQueries = useCallback(async (forceRefresh = false) => {
        if (!isReady) return;
        if (!forceRefresh) {
            const hit = cache?.get<SavedQueriesCacheData>(cacheKey);
            if (hit) {
                setQueries(hit.data.queries);
                setTotalItems(hit.data.totalItems);
                setLoading(false);
                return;
            }
        }
        setLoading(true);
        setError(null);
        try {
            const queryParts: string[] = [];
            if (options.page) queryParts.push(`page=${options.page}`);
            if (options.itemsPerPage) queryParts.push(`itemsPerPage=${options.itemsPerPage}`);
            if (options.groupQueryIds) queryParts.push(`groupQueryIds=${options.groupQueryIds}`);
            if (options.isFromCompany !== undefined) queryParts.push(`isFromCompany=${options.isFromCompany}`);
            if (options.search?.trim()) queryParts.push(`search=${encodeURIComponent(options.search.trim())}`);

            const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
            const response: SavedQueriesResponse = await authFetch(`/getQueriesForUser${queryString}`, { method: 'GET' });
            const fetchedQueries = response.member || [];
            const fetchedTotal = response.totalItems || 0;
            setQueries(fetchedQueries);
            setTotalItems(fetchedTotal);
            cache?.set(cacheKey, { queries: fetchedQueries, totalItems: fetchedTotal });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur de récupération');
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, cacheKey, cache, options.page, options.itemsPerPage, options.groupQueryIds, options.isFromCompany, options.search]);

    useWorkspaceChange(() => {
        cache?.invalidate('saved_queries|');
        setQueries([]);
        setTotalItems(0);
    });

    useEffect(() => {
        fetchQueries();
    }, [fetchQueries]);

    return {
        queries,
        totalItems,
        loading,
        error,
        refetch: () => fetchQueries(true)
    };
};
